import React, { Fragment, useEffect, useMemo, useState } from "react";
import { CardBody, Row, Spinner, Table } from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useSelector } from "react-redux";

// Sayfalar, modallar vb.
import DebouncedInput from "../../../Components/Common/DebouncedInput";
import TablePagination from "../../../Components/Common/TablePagination";

// Sütun Tanımları
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

const PrimPaymentCustomerTable = () => {
  const { primPaymentCustomers, primPaymentCustomersLoading } = useSelector(
    (state) => {
      return {
        primPaymentCustomers: state.order.primPaymentCustomers,
        primPaymentCustomersLoading: state.order.primPaymentCustomersLoading,
      };
    },
    shallowEqual
  );

  // States
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (!primPaymentCustomers) {
      setData([]);
      return;
    }

    const filteredData = primPaymentCustomers?.filter((item) => {
      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          item.customerName,
          item.primFee,
          item.creationDate,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }
      return true;
    });

    setData(filteredData || []);
  }, [globalFilter, primPaymentCustomers]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("customerName", {
        header: "Müşteri Adı",
        size: "auto",
        minSize: 250,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-bold fs-12">
              {row.original.customerName}
            </div>
          );
        },
      }),

      columnHelper.accessor("primFee", {
        header: "QR Prim Ücreti",
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: ({ row }) => {
          const formattedFee = new Intl.NumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
          }).format(row.original.primFee || 0);

          return (
            <div className="text-truncate fw-medium fs-12">{formattedFee}</div>
          );
        },
      }),

      columnHelper.accessor("creationDate", {
        header: "Oluşturulma Tarihi",
        size: 180,
        minSize: 180,
        maxSize: 180,
        cell: ({ row }) => {
          const formattedDate = row.original.creationDate
            ? new Date(row.original.creationDate).toLocaleString("tr-TR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-";
          return (
            <div className="text-truncate fw-medium fs-12">{formattedDate}</div>
          );
        },
      }),
    ],
    []
  );

  // Hooks
  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  return (
    <Fragment>
      <Row>
        <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
          <form>
            <div className="d-flex flex-wrap justify-content-between">
              <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                <div style={{ width: "100px" }}>
                  <select
                    className="form-select mb-3"
                    aria-label="Sayfa başına kayıt sayısı"
                    value={getState().pagination.pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {pageSizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <div
                  className="search-box me-2 mb-2 d-inline-block border"
                  style={{ borderRadius: "0.25em" }}
                >
                  <DebouncedInput
                    value={globalFilter ?? ""}
                    onChange={(value) => setGlobalFilter(String(value))}
                    placeholder={"Ara..."}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </div>
            </div>
          </form>
        </CardBody>
      </Row>

      <div
        style={{
          overflowX: "auto",
        }}
      >
        <Table className="align-middle">
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="text-muted"
                    style={{
                      width: header.column.columnDef.size,
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      letterSpacing: "0.8px",
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <i className="ri-arrow-down-s-line"></i>,
                          desc: <i className="ri-arrow-up-s-line"></i>,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {primPaymentCustomersLoading ? (
              <tr>
                <td colSpan="100%" className="text-center pt-5 pb-5">
                  <Spinner color="primary" />
                  <p className="text-dark">Yükleniyor...</p>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="100%" className="text-center pt-5 pb-5 text-muted">
                  <h1>
                    <i className="ri-error-warning-line text-muted"></i>
                  </h1>
                  <p>Kayıt bulunamadı!</p>
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.columnDef.size,
                            minWidth: cell.column.columnDef.minSize,
                            maxWidth: cell.column.columnDef.maxSize,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <TablePagination table={table} />
    </Fragment>
  );
};

export default PrimPaymentCustomerTable;
